import React, { FormEvent, RefAttributes } from "react";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { ReactComponent as SvgLogo } from "../../logo.svg";
import {
  Grid,
  MenuItem,
  Typography,
  Button,
  FormControl,
  FormControlLabel,
  Checkbox,
  TextField,
  Select,
  InputLabel,
} from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import moment, { Moment } from "moment-timezone";
import { GuestInfo, GuestInfoErrors } from "../../model/guest-info";
import d from "debug";
import { CalendarToday, CheckCircle } from "@material-ui/icons";
import CircleUnchecked from "@material-ui/icons/RadioButtonUnchecked";
import { requiredValidationRule, requiredEmailValidationRule, phoneValidationRule } from "../../utils/validationHelper";
import { guestInfoFields } from "../constants";
import NumberFormat from "react-number-format";

const debug = d("GuestCard");

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minWidth: "500px",
      maxWidth: "800px",
    },
    form: {
      "& .MuiTextField-root": {
        margin: theme.spacing(1),
      },
    },
    formControl: {
      width: "100%",
    },
    formInput: {
      width: "100%",
      backgroundColor: "white",
    },
    calendarInput: {
      width: "100%",
      backgroundColor: "white",
      paddingTop: "0px",
      paddingBottom: "0px",
      margingTop: "0px !important",
      marginBottom: "0px",
      fontSize: "14px !important",
    },
    hiddenButton: {
      visibility: "hidden",
      width: 1,
      height: 1,
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
  })
);

export interface GuestInfoCardProps {
  submitRef: React.RefObject<HTMLButtonElement>;
  guestInfo: GuestInfo;
  guestInfoErrors?: GuestInfoErrors;
  validateNow?: number; // set to random number to trigger validation
  guestInfoChanged: (guestInfo: GuestInfo, guestInfoErrors: GuestInfoErrors) => void;
  onSiteTours: boolean;
  contactMeToSchedule: boolean;
  selectedTourType: string;
}

const GuestInfoCard: React.FC<GuestInfoCardProps & RefAttributes<GuestInfoCardProps>> = ({
  submitRef,
  guestInfoChanged,
  guestInfo,
  guestInfoErrors,
  validateNow,
  onSiteTours,
  contactMeToSchedule,
  selectedTourType
}) => {
  const classes = useStyles();
  const [guestCardId] = React.useState<string | null | undefined>(guestInfo.guestCardId);
  const [bedrooms, setBedrooms] = React.useState<string | null | undefined>(guestInfo.minBedrooms || "");
  const [bedroomsError, setBedroomsError] = React.useState<string | null>(guestInfoErrors?.bedroomsError);
  const [bathrooms, setBathrooms] = React.useState<string | null | undefined>(guestInfo.minBathrooms || "");
  const [bathroomsError, setBathroomsError] = React.useState<string | null>(guestInfoErrors?.bathroomsError);
  const [firstName, setFirstName] = React.useState<string | undefined>(guestInfo.firstName || "");
  const [firstNameError, setFirstNameError] = React.useState<string | null>(guestInfoErrors?.firstNameError);
  const [lastName, setLastName] = React.useState<string | undefined>(guestInfo.lastName || "");
  const [lastNameError, setLastNameError] = React.useState<string | null>(guestInfoErrors?.lastNameError);
  const [email, setEmail] = React.useState<string | undefined>(guestInfo.emailAddress || "");
  const [emailError, setEmailError] = React.useState<string | null>(guestInfoErrors?.emailError);
  const [phone, setPhone] = React.useState<string | undefined>(guestInfo.mobilePhone || "");
  const [phoneError, setPhoneError] = React.useState<string | null>(guestInfoErrors?.phoneError);
  const [smsOptIn, setSmsOptIn] = React.useState<boolean | undefined>(guestInfo.smsOptIn);
  const [tourType, setTourType] = React.useState<string>(selectedTourType);
  const [tourTypeError, setTourTypeError] = React.useState<string | null>(guestInfoErrors?.tourTypeError);
  const [moveInDate, setMoveInDate] = React.useState<Date | null | undefined>(guestInfo.desiredMoveInDate || null);
  const [moveInDateError, setMoveInDateError] = React.useState<string | null>(guestInfoErrors?.moveInDateError);
  const [minMoveInDate] = React.useState<Moment>(moment().add(1, "day"));
  const [tourNotes, setTourNotes] = React.useState<string>(guestInfo?.tourNotes || "");
  const [fieldIdNames] = React.useState<Map<string, string>>(guestInfoFields);

  React.useEffect(() => {
    if (guestInfo) {
            setFirstName(guestInfo.firstName);
            setBedrooms(guestInfo.minBedrooms);
            setBathrooms(guestInfo.minBathrooms);
            setLastName(guestInfo.lastName);
            setEmail(guestInfo.emailAddress);
            setPhone(guestInfo.mobilePhone);
            setSmsOptIn(guestInfo.smsOptIn);
            setTourType(selectedTourType);
            setMoveInDate(guestInfo.desiredMoveInDate);
            setTourNotes(guestInfo?.tourNotes);
     }
  }, [guestInfo]);

  React.useEffect(() => {
    if (validateNow > 0) {
      guestInfoChanged(getGuestInfo(), validateAll());
    }
  }, [validateNow]);

  const getGuestInfo = (): GuestInfo => {
    return {
      firstName,
      lastName,
      emailAddress: email,
      mobilePhone: phone,
      smsOptIn,
      desiredMoveInDate: moveInDate,
      minBedrooms: bedrooms,
      minBathrooms: bathrooms,
      tourType: selectedTourType,
      guestCardId,
      tourNotes,
    };
  };

  const getGuestInfoErrors = (): GuestInfoErrors => {
    return {
      firstNameError,
      lastNameError,
      emailError,
      phoneError,
      moveInDateError,
      bedroomsError,
      bathroomsError,
      tourTypeError,
    };
  };

  const anyErrors = () => {
    return (
      firstNameError || lastNameError || emailError || phoneError || moveInDateError || tourTypeError || bedroomsError || bathroomsError
    );
  };

  const getFieldIdFromName = (name: string): string | null => {
    let id: string = null;
    fieldIdNames.forEach((value, key) => {
      if (value === name) {
        id = key;
      }
    });
    return id;
  };

  const validateAll = (): GuestInfoErrors => {
    const errors = getGuestInfoErrors();
    fieldIdNames.forEach((value, key) => {
      const errorText = validate(key, value, null, true);
      errors[key + "Error"] = errorText;
    });
    return errors;
  };

  const validate = (id: string, name: string, value: any, useStateValue: boolean = false): string => {
    // console.log(`validate(${id}, ${name}, ${value})`);
    if (!id && name) {
      id = getFieldIdFromName(name);
    }
    if (!name && id) {
      name = fieldIdNames.get(id);
    }
    let error = null;
    switch (id) {
      case "firstName": {
        error = requiredValidationRule(name, !useStateValue ? value : firstName);
        setFirstNameError(error);
        break;
      }
      case "lastName": {
        error = requiredValidationRule(name, !useStateValue ? value : lastName);
        setLastNameError(error);
        break;
      }
      case "email": {
        error = requiredEmailValidationRule(name, !useStateValue ? value : email);
        setEmailError(error);
        break;
      }
      case "phone": {
        const phoneValue = !useStateValue ? value : phone; // only validate if phone has be entered.
        error = phoneValue ? phoneValidationRule(name, !useStateValue ? value : phone) : null;
        setPhoneError(error);
        break;
      }
      case "bedrooms": {
        error = requiredValidationRule(name, !useStateValue ? value : bedrooms);
        setBedroomsError(error);
        break;
      }
      case "bathrooms": {
        error = requiredValidationRule(name, !useStateValue ? value : bathrooms);
        setBathroomsError(error);
        break;
      }
      case "moveInDate": {
        error = requiredValidationRule(name, !useStateValue ? value : moveInDate);
        setMoveInDateError(error);
        break;
      }
      case "tourType": {
        error = requiredValidationRule(name, !useStateValue ? value : tourType);
        setTourTypeError(error);
        break;
      }
    }
    return error;
  };

  const validateField = (event: React.ChangeEvent<{ value: unknown; name: string; id: string }>) => {
    validate(event.target.id, event.target.name, event.target.value);
  };

  const handleMoveInDateChange = (date: Date | null) => {
    setMoveInDate(date || undefined);
    setMoveInDateError(requiredValidationRule(fieldIdNames.get("moveInDate"), date));
    const newGuestInfo = getGuestInfo();
    newGuestInfo.desiredMoveInDate = date ? date : null;
    const newErrors = getGuestInfoErrors();
    newErrors.moveInDateError = requiredValidationRule(fieldIdNames.get("moveInDate"), date);
    guestInfoChanged(newGuestInfo, newErrors);
  };

  const handleFirstNameChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFirstName(event.target.value as string);
    const newGuestInfo = getGuestInfo();
    newGuestInfo.firstName = event.target.value as string;
    guestInfoChanged(newGuestInfo, getGuestInfoErrors());
  };

  const handleLastNameChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setLastName(event.target.value as string);
    const newGuestInfo = getGuestInfo();
    newGuestInfo.lastName = event.target.value as string;
    guestInfoChanged(newGuestInfo, getGuestInfoErrors());
  };

  const handlePhoneChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setPhone(event.target.value as string);
    const newGuestInfo = getGuestInfo();
    newGuestInfo.mobilePhone = event.target.value as string;
    const newErrors = getGuestInfoErrors();
    newErrors.phoneError = validate("phone", newGuestInfo.mobilePhone, null);
    guestInfoChanged(newGuestInfo, newErrors);
  };

  const handleEmailChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setEmail(event.target.value as string);
    const newGuestInfo = getGuestInfo();
    newGuestInfo.emailAddress = event.target.value as string;
    const newErrors = getGuestInfoErrors();
    newErrors.emailError = validate("email", newGuestInfo.emailAddress, null);
    guestInfoChanged(newGuestInfo, newErrors);
  };

  const handleBedRoomsChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setBedrooms(event.target.value as string);
    const newGuestInfo = getGuestInfo();
    newGuestInfo.minBedrooms = event.target.value as string;
    guestInfoChanged(newGuestInfo, getGuestInfoErrors());
    validate("bedrooms", fieldIdNames.get("bedrooms"), event.target.value);
  };

  const handleBathRoomsChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setBathrooms(event.target.value as string);
    const newGuestInfo = getGuestInfo();
    newGuestInfo.minBathrooms = event.target.value as string;
    guestInfoChanged(newGuestInfo, getGuestInfoErrors());
    validate("bathrooms", fieldIdNames.get("bathrooms"), event.target.value);
  };

  const handleTourNoteChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setTourNotes(event.target.value as string);
    const newGuestInfo = getGuestInfo();
    newGuestInfo.tourNotes = event.target.value as string;
    guestInfoChanged(newGuestInfo, getGuestInfoErrors());
  };

  const handleOptInChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSmsOptIn(event.target.checked);
    const newGuestInfo = getGuestInfo();
    newGuestInfo.smsOptIn = event.target.checked;
    guestInfoChanged(newGuestInfo, getGuestInfoErrors());
  };

  const handleSubmit = (event: FormEvent<Element>) => {
    event.preventDefault();
  };

  return (
    <Grid container direction="column" justify="center" alignItems="center" spacing={1}>
      <Grid item>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <form className={classes.root} onSubmit={handleSubmit}>
            <Grid container direction="column" justify="center" alignItems="center">
              <Grid item xs={12}>
                <SvgLogo />
              </Grid>
              <Grid item xs={12}>
                <Typography gutterBottom variant="subtitle1" component="h1">
                  Guest Information
                </Typography>
              </Grid>
              <Grid item xs={12} container direction="row" justify="flex-start" alignItems="flex-start" spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    className={classes.formInput}
                    label={firstNameError ? firstNameError : fieldIdNames.get("firstName") + "*"}
                    onChange={handleFirstNameChange}
                    onBlur={(e) => validateField(e)}
                    name={fieldIdNames.get("firstName")}
                    id="firstName"
                    value={firstName}
                    error={!!firstNameError}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    className={classes.formInput}
                    label={lastNameError ? lastNameError : fieldIdNames.get("lastName") + "*"}
                    onChange={handleLastNameChange}
                    onBlur={(e) => validateField(e)}
                    name={fieldIdNames.get("lastName")}
                    id="lastName"
                    value={lastName}
                    error={!!lastNameError}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    className={classes.formInput}
                    label={emailError ? emailError : fieldIdNames.get("email") + "*"}
                    onChange={handleEmailChange}
                    onBlur={(e) => validateField(e)}
                    name={fieldIdNames.get("email")}
                    id="email"
                    value={email}
                    error={!!emailError}
                  />
                </Grid>
                <Grid item container direction="row" justify="space-between" alignItems="flex-start" xs={12} sm={6} spacing={1}>
                  <Grid item xs={6}>
                    <NumberFormat
                      customInput={TextField}
                      format="(###) ###-####"
                      variant="outlined"
                      className={classes.formInput}
                      label={phoneError ? phoneError : "Phone Number"}
                      onChange={handlePhoneChange}
                      onBlur={(e) => validateField(e)}
                      name={fieldIdNames.get("phone")}
                      id="phone"
                      value={phone}
                      error={!!phoneError}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl className={classes.formControl} variant="outlined" style={{ marginTop: "0px" }}>
                      <FormControlLabel
                        style={{ width: "100%" }}
                        control={
                          <Checkbox
                            checked={smsOptIn}
                            icon={<CircleUnchecked />}
                            checkedIcon={<CheckCircle />}
                            onChange={handleOptInChange}
                            name="smsOptIn"
                          />
                        }
                        label="OK to receive texts?"
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <KeyboardDatePicker
                    className={classes.calendarInput}
                    keyboardIcon={<CalendarToday />}
                    variant="inline"
                    inputVariant="outlined"
                    minDate={minMoveInDate.toDate()}
                    disableToolbar
                    autoOk={true}
                    format="MM/dd/yyyy"
                    minDateMessage="move-in must be in the future"
                    id="moveInDate"
                    label={moveInDateError ? moveInDateError : fieldIdNames.get("moveInDate")}
                    name={fieldIdNames.get("moveInDate")}
                    value={moveInDate}
                    error={!!moveInDateError}
                    onChange={handleMoveInDateChange}
                    onBlur={(e) => validateField(e)}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl variant="outlined" className={classes.formControl} error={!!bedroomsError}>
                    <InputLabel id="bedroomsLabel" error={!!bedroomsError}>
                      {bedroomsError ? bedroomsError : fieldIdNames.get("bedrooms") + "*"}
                    </InputLabel>
                    <Select
                      variant="outlined"
                      className={classes.formInput}
                      labelId="bedroomsLabel"
                      label={bedroomsError ? bedroomsError : fieldIdNames.get("bedrooms") + "*"}
                      onChange={handleBedRoomsChange}
                      onBlur={(e) => validateField(e)}
                      name={fieldIdNames.get("bedrooms")}
                      id="bedrooms"
                      value={bedrooms}
                      error={!!bedroomsError}
                    >
                      <MenuItem value={"studio"}>Studio</MenuItem>
                      <MenuItem value={"1"}>1</MenuItem>
                      <MenuItem value={"2"}>2</MenuItem>
                      <MenuItem value={"3"}>3</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl variant="outlined" className={classes.formControl} error={!!bathroomsError}>
                    <InputLabel id="bathroomsLabel" error={!!bathroomsError}>
                      {bathroomsError ? bathroomsError : fieldIdNames.get("bathrooms") + "*"}
                    </InputLabel>
                    <Select
                      variant="outlined"
                      className={classes.formInput}
                      labelId="bathroomsLabel"
                      label={bathroomsError ? bathroomsError : fieldIdNames.get("bathrooms") + "*"}
                      onChange={handleBathRoomsChange}
                      onBlur={(e) => validateField(e)}
                      name={fieldIdNames.get("bathrooms")}
                      id="bathrooms"
                      value={bathrooms}
                      error={!!bathroomsError}
                    >
                      <MenuItem value={"1"}>1</MenuItem>
                      <MenuItem value={"2"}>2</MenuItem>
                      <MenuItem value={"3"}>3</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    multiline={true}
                    inputProps={{ maxLength: 255 }}
                    variant="outlined"
                    className={classes.formInput}
                    onChange={handleTourNoteChange }
                    name={fieldIdNames.get("tourNotes")}
                    id="tourNotes"
                    value={tourNotes}
                    label={"Tour Comments"}
                  />
                </Grid>
              </Grid>
              <Button ref={submitRef} type="submit" className={classes.hiddenButton}>
                validate
              </Button>
            </Grid>
          </form>
        </MuiPickersUtilsProvider>
      </Grid>
    </Grid>
  );
};

export default GuestInfoCard;